import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  useTheme,
  alpha,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Brush,
  ReferenceArea,
  ReferenceLine,
} from 'recharts';
import {
  ZoomIn,
  ZoomOut,
  NavigateBefore,
  NavigateNext,
  RestartAlt,
} from '@mui/icons-material';
import { DatosHistoricosResponse } from '../../types';
import { formatYAxisTick } from '../../utils/chartUtils';

/** Parsea timestamp de punto histórico a segundos (epoch o ISO). */
function parseHistoricTimestampSeconds(ts: string): number {
  const num = Number(ts);
  if (!isNaN(num) && num > 0 && num < 1e12) return num;
  return new Date(ts).getTime() / 1000;
}

function interpolateSegment(t: number, t0: number, v0: number, t1: number, v1: number): number {
  if (t1 === t0) return v0;
  return v0 + ((t - t0) / (t1 - t0)) * (v1 - v0);
}

/** Interpola una serie temporal ordenada en el instante t (p. ej. valor de fuerza en t buscado). */
function interpolateSeriesAtTime(t: number, points: { t: number; v: number }[]): number | null {
  if (points.length === 0) return null;
  if (t <= points[0].t) return points[0].v;
  if (t >= points[points.length - 1].t) return points[points.length - 1].v;
  let lo = 0;
  let hi = points.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (points[mid].t <= t) lo = mid;
    else hi = mid;
  }
  return interpolateSegment(t, points[lo].t, points[lo].v, points[hi].t, points[hi].v);
}

export interface ChartSeries {
  key: string;
  label: string;
  color: string;
  yAxisId: 'primary' | 'secondary';
  dataKey: string;
  unidad?: string;
}

export interface YAxisConfig {
  id: 'primary' | 'secondary';
  label: string;
  orientation?: 'left' | 'right';
  tickFormatter?: (value: number) => string;
  min?: number;
  max?: number;
}

export interface ReferenceLineConfig {
  y: number;
  stroke: string;
  strokeDasharray?: string;
  label?: string;
  yAxisId?: 'primary' | 'secondary';
}

export interface AdvancedLineChartProps {
  title?: string;
  data: DatosHistoricosResponse | null;
  series: ChartSeries[];
  yAxes: YAxisConfig[];
  isLoading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  height?: number;
  primaryColor?: string;
  secondaryColor?: string;
  referenceLines?: ReferenceLineConfig[];
  isWidget?: boolean;
  widgetSize?: 'small' | 'medium' | 'large' | 'xlarge' | { columnas: number; filas: number };
  xAxisMode?: 'time' | 'seconds'; // 'time' para fechas, 'seconds' para tiempo en segundos
  xAxisLabel?: string; // Etiqueta personalizada para el eje X
  /** Cuando se usa una variable como eje X (ej: Fuerza vs Desplazamiento). Requiere timestamps alineados. */
  xAxisVariableKey?: string; // dataKey de la variable para el eje X (ej: 'fuerza_actuador')
  xAxisVariableLabel?: string; // Etiqueta del eje X cuando es variable (ej: 'Fuerza (kN)')
  /** Offset en segundos a aplicar a las variables de sensor (no actuador) en el eje temporal. */
  sensorTimeOffsetSeconds?: number;
  /**
   * Datos tal como los usa Recharts (filas sparse por serie), para exportar Excel alineado con el dibujo.
   * Se llama tras render; el padre debe guardarlo en un ref (no en estado) para no re-renderizar en bucle.
   */
  onChartRowsForExport?: (
    rows: Record<string, string | number | null | undefined>[],
    series: ChartSeries[]
  ) => void;
}

export const AdvancedLineChart: React.FC<AdvancedLineChartProps> = ({
  title,
  data,
  series,
  yAxes,
  isLoading = false,
  error = null,
  emptyMessage,
  height = 400,
  primaryColor: _primaryColor,
  secondaryColor: _secondaryColor,
  referenceLines = [],
  isWidget = false,
  widgetSize,
  xAxisMode = 'time',
  xAxisLabel,
  xAxisVariableKey,
  xAxisVariableLabel,
  sensorTimeOffsetSeconds = 0,
  onChartRowsForExport,
}) => {
  // const { t } = useTranslation(); // No utilizado actualmente
  const theme = useTheme();
  const [zoomDomain, setZoomDomain] = useState<[number, number] | null>(null);
  const [brushStartIndex, setBrushStartIndex] = useState<number>(0);
  const [brushEndIndex, setBrushEndIndex] = useState<number>(0);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<number | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<number | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<number | null>(null);
  const [selectionPixelStart, setSelectionPixelStart] = useState<number | null>(null);
  const [selectionPixelEnd, setSelectionPixelEnd] = useState<number | null>(null);
  const chartRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isBrushChangingRef = useRef<boolean>(false);
  const lastBrushIndicesRef = useRef<{ startIndex: number; endIndex: number } | null>(null);
  const brushUpdateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showBrush = !isWidget || (
    typeof widgetSize === 'object' && 'columnas' in widgetSize 
      ? widgetSize.columnas === 4 
      : widgetSize === 'xlarge'
  );

  const chartData = useMemo(() => {
    if (!data || !data.variables || data.variables.length === 0 || series.length === 0) {
      return [];
    }

    const relevantVariables = data.variables.filter((variable) => {
      const dataKey = `${variable.variable_id}_${variable.sensor_id}`.replace(/[^a-zA-Z0-9_]/g, '_');
      return series.some((serie) => serie.dataKey === dataKey);
    });

    const xAxisVariable = xAxisVariableKey
      ? data.variables.find((v) => {
          const dk = `${v.variable_id}_${v.sensor_id}`.replace(/[^a-zA-Z0-9_]/g, '_');
          return dk === xAxisVariableKey;
        })
      : null;
    const allRelevant = xAxisVariable && !relevantVariables.some(
      (v) => `${v.variable_id}_${v.sensor_id}`.replace(/[^a-zA-Z0-9_]/g, '_') === xAxisVariableKey
    )
      ? [...relevantVariables, xAxisVariable]
      : relevantVariables;

    // Pre-build per-variable Maps: timestamp → dato (O(1) lookup instead of O(n) .find())
    const varMaps = new Map<string, { map: Map<string, { valor: number | null | undefined }>; isSensor: boolean }>();
    allRelevant.forEach((variable) => {
      const dk = `${variable.variable_id}_${variable.sensor_id}`.replace(/[^a-zA-Z0-9_]/g, '_');
      const m = new Map<string, { valor: number | null | undefined }>();
      variable.datos.forEach((d) => {
        if (d.timestamp) m.set(d.timestamp, d);
      });
      varMaps.set(dk, { map: m, isSensor: variable.sensor_id !== 'actuador' });
    });

    const xAxisSortedPoints: { t: number; v: number }[] =
      xAxisVariableKey && xAxisVariable
        ? xAxisVariable.datos
            .filter((d) => d.timestamp && d.valor !== null && d.valor !== undefined)
            .map((d) => ({
              t: parseHistoricTimestampSeconds(d.timestamp!),
              v: Number(d.valor),
            }))
            .sort((a, b) => a.t - b.t)
        : [];

    // Build per-variable sorted data arrays to avoid the union-then-lookup pattern.
    // Each variable produces its own rows; rows are merged at the end for the chart.
    const offset = sensorTimeOffsetSeconds || 0;
    const allRows: Record<string, number | string | null>[] = [];

    allRelevant.forEach((variable) => {
      const dk = `${variable.variable_id}_${variable.sensor_id}`.replace(/[^a-zA-Z0-9_]/g, '_');
      const isSensor = variable.sensor_id !== 'actuador';

      variable.datos.forEach((d) => {
        if (!d.timestamp || d.valor === null || d.valor === undefined) return;

        let timeValue: number;
        if (xAxisVariableKey && xAxisVariable) {
          if (isSensor && offset !== 0 && xAxisSortedPoints.length > 0) {
            const base = parseHistoricTimestampSeconds(d.timestamp);
            const tAdj = base + offset;
            const xv = interpolateSeriesAtTime(tAdj, xAxisSortedPoints);
            if (xv == null || !isFinite(xv)) return;
            timeValue = xv;
          } else {
            const xEntry = varMaps.get(xAxisVariableKey);
            const xDato = xEntry?.map.get(d.timestamp);
            if (!xDato || xDato.valor === null || xDato.valor === undefined) return;
            timeValue = Number(xDato.valor);
          }
        } else if (xAxisMode === 'seconds') {
          const num = Number(d.timestamp);
          timeValue = (!isNaN(num) && num > 0 && num < 1e12) ? num : new Date(d.timestamp).getTime() / 1000;
          if (isSensor && offset !== 0) timeValue += offset;
        } else {
          const num = Number(d.timestamp);
          timeValue = (!isNaN(num) && num > 0 && num < 1e12) ? num * 1000 : new Date(d.timestamp).getTime();
          if (isSensor && offset !== 0) timeValue += offset * 1000;
        }

        const row: Record<string, number | string | null> = {
          time: timeValue,
          timestamp: d.timestamp,
          [dk]: Number(d.valor),
        };
        allRows.push(row);
      });
    });

    allRows.sort((a, b) => (a.time as number) - (b.time as number));
    return allRows;
  }, [data, series, xAxisMode, xAxisVariableKey, sensorTimeOffsetSeconds]);

  const sortedData = useMemo(() => {
    if (!chartData || chartData.length === 0) return [];
    if (xAxisVariableKey) {
      return chartData;
    }
    return [...chartData].sort((a, b) => {
      const aTime = typeof a.time === 'number' ? a.time : 0;
      const bTime = typeof b.time === 'number' ? b.time : 0;
      return aTime - bTime;
    });
  }, [chartData, xAxisVariableKey]);

  useEffect(() => {
    if (!onChartRowsForExport) return;
    if (!sortedData || sortedData.length === 0 || !series || series.length === 0) {
      onChartRowsForExport([], series || []);
      return;
    }
    onChartRowsForExport(sortedData as Record<string, string | number | null | undefined>[], series);
  }, [sortedData, series, onChartRowsForExport]);

  // Calcular número óptimo de ticks para el eje X basado en el rango de datos
  const xAxisTickCount = useMemo(() => {
    if (!sortedData || sortedData.length === 0) return 5;
    
    const times = sortedData.map(d => typeof d.time === 'number' ? d.time : 0).filter(t => t > 0);
    if (times.length === 0) return 5;
    
    const minTime = Math.min(...times);
    const maxTime = Math.max(...times);
    const range = maxTime - minTime;
    
    if (range === 0) return 5;
    
    // Calcular número de ticks basado en el rango
    // Para pantallas completas, usar más ticks (8-12)
    // Para widgets, usar menos ticks (4-6)
    const baseTickCount = isWidget ? 5 : 8;
    
    // Ajustar según el rango: si el rango es muy grande, reducir ticks
    // Si el rango es pequeño, mantener más ticks
    let tickCount = baseTickCount;
    
    if (xAxisMode === 'seconds') {
      // Para segundos, ajustar según la magnitud del rango
      if (range > 10000) tickCount = Math.max(4, baseTickCount - 2);
      else if (range > 1000) tickCount = baseTickCount;
      else if (range > 100) tickCount = Math.min(10, baseTickCount + 1);
      else tickCount = Math.min(12, baseTickCount + 2);
    } else {
      // Para fechas, ajustar según días
      const daysRange = range / (1000 * 60 * 60 * 24);
      if (daysRange > 30) tickCount = Math.max(4, baseTickCount - 2);
      else if (daysRange > 7) tickCount = baseTickCount;
      else if (daysRange > 1) tickCount = Math.min(10, baseTickCount + 1);
      else tickCount = Math.min(12, baseTickCount + 2);
    }
    
    return tickCount;
  }, [sortedData, xAxisMode, isWidget]);

  // Calcular número óptimo de ticks para los ejes Y basado en el rango de datos
  const calculateYAxisTickCount = useCallback((domain: [number, number] | ['auto', 'auto'] | undefined) => {
    // Si el dominio es undefined, usar valores por defecto
    if (!domain) {
      return isWidget ? 4 : 6;
    }
    // Verificar si el dominio es ['auto', 'auto'] usando type guard
    const isAutoDomain = (d: [number, number] | ['auto', 'auto']): d is ['auto', 'auto'] => {
      return d[0] === 'auto' && d[1] === 'auto';
    };
    if (isAutoDomain(domain)) {
      return isWidget ? 4 : 6;
    }
    // En este punto, TypeScript sabe que domain es [number, number]
    // Verificar que ambos valores sean números (redundante pero seguro)
    if (typeof domain[0] !== 'number' || typeof domain[1] !== 'number') {
      return isWidget ? 4 : 6;
    }
    
    const min = typeof domain[0] === 'number' ? domain[0] : 0;
    const max = typeof domain[1] === 'number' ? domain[1] : 0;
    const range = max - min;
    
    if (range === 0 || !isFinite(range)) {
      return isWidget ? 4 : 6;
    }
    
    // Calcular número de ticks basado en el rango
    const baseTickCount = isWidget ? 4 : 6;
    
    // Ajustar según la magnitud del rango
    if (range > 1000) return Math.max(4, baseTickCount - 1);
    if (range > 100) return baseTickCount;
    if (range > 10) return Math.min(8, baseTickCount + 1);
    if (range > 1) return Math.min(10, baseTickCount + 2);
    return Math.min(12, baseTickCount + 3);
  }, [isWidget]);

  React.useEffect(() => {
    if (sortedData.length > 0 && showBrush) {
      setBrushEndIndex(sortedData.length - 1);
    }
  }, [sortedData.length, showBrush]);

  React.useEffect(() => {
    return () => {
      if (brushUpdateTimeoutRef.current) {
        clearTimeout(brushUpdateTimeoutRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (!showBrush) return;
    
    if (isBrushChangingRef.current) {
      return;
    }

    if (lastBrushIndicesRef.current) {
      if (
        brushStartIndex === lastBrushIndicesRef.current.startIndex &&
        brushEndIndex === lastBrushIndicesRef.current.endIndex
      ) {
        return;
      }
    }

    if (!zoomDomain || !sortedData || sortedData.length === 0) {
      lastBrushIndicesRef.current = null;
      setBrushStartIndex(0);
      setBrushEndIndex(sortedData.length - 1);
      return;
    }

    let startIdx = 0;
    let endIdx = sortedData.length - 1;

    for (let i = 0; i < sortedData.length; i++) {
      const item = sortedData[i];
      if (!item) continue;
      const timeValue = typeof item.time === 'number' ? item.time : null;
      if (timeValue !== null && timeValue >= zoomDomain[0]) {
        startIdx = i;
        break;
      }
    }

    for (let i = sortedData.length - 1; i >= 0; i--) {
      const item = sortedData[i];
      if (!item) continue;
      const timeValue = typeof item.time === 'number' ? item.time : null;
      if (timeValue !== null && timeValue <= zoomDomain[1]) {
        endIdx = i;
        break;
      }
    }

    if (startIdx !== brushStartIndex || endIdx !== brushEndIndex) {
      setBrushStartIndex(startIdx);
      setBrushEndIndex(endIdx);
    }
  }, [zoomDomain, sortedData, brushStartIndex, brushEndIndex, showBrush]);

  const xDomain = useMemo(() => {
    if (!sortedData || sortedData.length === 0) return ['dataMin', 'dataMax'];

    if (zoomDomain && zoomDomain.length === 2 && zoomDomain[0] > 0 && zoomDomain[1] > 0) {
      const [start, end] = zoomDomain[0] < zoomDomain[1] ? zoomDomain : [zoomDomain[1], zoomDomain[0]];
      return [start, end] as [number, number];
    }

    const timeValues = sortedData
      .map((item) => item.time)
      .filter((val) => val !== null && val !== undefined)
      .map((val) => (typeof val === 'number' ? val : 0))
      .filter((val) => val > 0);

    if (timeValues.length === 0) return ['dataMin', 'dataMax'];

    const min = Math.min(...timeValues);
    const max = Math.max(...timeValues);

    return [min, max] as [number, number];
  }, [sortedData, zoomDomain]);

  const visibleData = useMemo(() => {
    return sortedData;
  }, [sortedData]);

  const hasData = useMemo(() => {
    if (!sortedData || sortedData.length === 0) return false;
    return sortedData.some((item) =>
      series.some((serie) => item[serie.dataKey] !== null && item[serie.dataKey] !== undefined)
    );
  }, [sortedData, series]);

  const yAxisDataRanges = useMemo(() => {
    const ranges: { primary?: { min: number; max: number }; secondary?: { min: number; max: number } } = {};

    const primaryValues: number[] = [];
    sortedData.forEach((item) => {
      series.forEach((serie) => {
        const value = item[serie.dataKey];
        if (value !== null && value !== undefined && typeof value === 'number') {
          if (serie.yAxisId === 'primary') {
            primaryValues.push(value);
          }
        }
      });
    });
    if (primaryValues.length > 0) {
      ranges.primary = {
        min: Math.min(...primaryValues),
        max: Math.max(...primaryValues),
      };
    }

    const secondaryValues: number[] = [];
    sortedData.forEach((item) => {
      series.forEach((serie) => {
        const value = item[serie.dataKey];
        if (value !== null && value !== undefined && typeof value === 'number') {
          if (serie.yAxisId === 'secondary') {
            secondaryValues.push(value);
          }
        }
      });
    });
    if (secondaryValues.length > 0) {
      ranges.secondary = {
        min: Math.min(...secondaryValues),
        max: Math.max(...secondaryValues),
      };
    }

    return ranges;
  }, [sortedData, series]);

  const yAxisDomains = useMemo(() => {
    const domains: { primary?: [number, number]; secondary?: [number, number] } = {};

    const primaryAxisConfig = yAxes.find((axis) => axis.id === 'primary');
    const secondaryAxisConfig = yAxes.find((axis) => axis.id === 'secondary');

    if (primaryAxisConfig && primaryAxisConfig.min !== undefined && primaryAxisConfig.max !== undefined) {
      domains.primary = [primaryAxisConfig.min, primaryAxisConfig.max];
    } else {
      const primaryValues: number[] = [];

      sortedData.forEach((item) => {
        series.forEach((serie) => {
          const value = item[serie.dataKey];
          if (value !== null && value !== undefined && typeof value === 'number') {
            if (serie.yAxisId === 'primary') {
              primaryValues.push(value);
            }
          }
        });
      });

      referenceLines.forEach((refLine) => {
        if (refLine.y !== null && refLine.y !== undefined && typeof refLine.y === 'number') {
          const axisId = refLine.yAxisId || 'primary';
          if (axisId === 'primary') {
            primaryValues.push(refLine.y);
          }
        }
      });

      if (primaryValues.length > 0) {
        const min = primaryAxisConfig?.min !== undefined ? primaryAxisConfig.min : Math.min(...primaryValues);
        const max = primaryAxisConfig?.max !== undefined ? primaryAxisConfig.max : Math.max(...primaryValues);
        const range = max - min;
        const padding = range * 0.1 || Math.abs(max) * 0.1 || 1;
        domains.primary = [
          primaryAxisConfig?.min !== undefined ? primaryAxisConfig.min : min - padding,
          primaryAxisConfig?.max !== undefined ? primaryAxisConfig.max : max + padding
        ];
      }
    }

    if (secondaryAxisConfig && secondaryAxisConfig.min !== undefined && secondaryAxisConfig.max !== undefined) {
      domains.secondary = [secondaryAxisConfig.min, secondaryAxisConfig.max];
    } else {
      const secondaryValues: number[] = [];

      sortedData.forEach((item) => {
        series.forEach((serie) => {
          const value = item[serie.dataKey];
          if (value !== null && value !== undefined && typeof value === 'number') {
            if (serie.yAxisId === 'secondary') {
              secondaryValues.push(value);
            }
          }
        });
      });

      referenceLines.forEach((refLine) => {
        if (refLine.y !== null && refLine.y !== undefined && typeof refLine.y === 'number') {
          const axisId = refLine.yAxisId || 'primary';
          if (axisId === 'secondary') {
            secondaryValues.push(refLine.y);
          }
        }
      });

      if (secondaryValues.length > 0) {
        const min = secondaryAxisConfig?.min !== undefined ? secondaryAxisConfig.min : Math.min(...secondaryValues);
        const max = secondaryAxisConfig?.max !== undefined ? secondaryAxisConfig.max : Math.max(...secondaryValues);
        const range = max - min;
        const padding = range * 0.1 || Math.abs(max) * 0.1 || 1;
        domains.secondary = [
          secondaryAxisConfig?.min !== undefined ? secondaryAxisConfig.min : min - padding,
          secondaryAxisConfig?.max !== undefined ? secondaryAxisConfig.max : max + padding
        ];
      }
    }

    return domains;
  }, [sortedData, series, referenceLines, yAxes]);

  const handleBrushChange = useCallback(
    (domain: { startIndex?: number; endIndex?: number } | null) => {
      if (!domain || domain.startIndex === undefined || domain.endIndex === undefined) {
        if (brushUpdateTimeoutRef.current) {
          clearTimeout(brushUpdateTimeoutRef.current);
          brushUpdateTimeoutRef.current = null;
        }
        isBrushChangingRef.current = false;
        lastBrushIndicesRef.current = null;
        setZoomDomain(null);
        setBrushStartIndex(0);
        setBrushEndIndex(sortedData.length - 1);
        return;
      }

      isBrushChangingRef.current = true;

      const newStartIndex = Math.max(0, Math.min(domain.startIndex, sortedData.length - 1));
      const newEndIndex = Math.max(0, Math.min(domain.endIndex, sortedData.length - 1));

      const finalStartIndex = Math.min(newStartIndex, newEndIndex);
      const finalEndIndex = Math.max(newStartIndex, newEndIndex);

      lastBrushIndicesRef.current = {
        startIndex: finalStartIndex,
        endIndex: finalEndIndex,
      };

      setBrushStartIndex(finalStartIndex);
      setBrushEndIndex(finalEndIndex);

      if (brushUpdateTimeoutRef.current) {
        clearTimeout(brushUpdateTimeoutRef.current);
      }

      brushUpdateTimeoutRef.current = setTimeout(() => {
        if (sortedData && sortedData.length > 0 && finalStartIndex < sortedData.length && finalEndIndex < sortedData.length) {
          const startItem = sortedData[finalStartIndex];
          const endItem = sortedData[finalEndIndex];
          const startTime = startItem && typeof startItem.time === 'number' ? startItem.time : null;
          const endTime = endItem && typeof endItem.time === 'number' ? endItem.time : null;

          if (startTime !== null && endTime !== null && startTime > 0 && endTime > 0 && startTime <= endTime) {
            setZoomDomain([startTime, endTime]);
          }
        }

        requestAnimationFrame(() => {
          isBrushChangingRef.current = false;
        });

        brushUpdateTimeoutRef.current = null;
      }, 150);
    },
    [sortedData]
  );

  const handleNavigateBack = useCallback(() => {
    if (!sortedData || sortedData.length === 0 || !zoomDomain) return;

    isBrushChangingRef.current = false;
    const currentRange = zoomDomain[1] - zoomDomain[0];
    const firstItem = sortedData[0];
    const firstTime = firstItem && typeof firstItem.time === 'number' ? firstItem.time : 0;
    const newStart = Math.max(firstTime, zoomDomain[0] - currentRange);
    const newEnd = newStart + currentRange;

    setZoomDomain([newStart, newEnd]);
  }, [sortedData, zoomDomain]);

  const handleNavigateForward = useCallback(() => {
    if (!sortedData || sortedData.length === 0 || !zoomDomain) return;

    isBrushChangingRef.current = false;
    const currentRange = zoomDomain[1] - zoomDomain[0];
    const lastItem = sortedData[sortedData.length - 1];
    const lastTime = lastItem && typeof lastItem.time === 'number' ? lastItem.time : 0;
    const newEnd = Math.min(lastTime, zoomDomain[1] + currentRange);
    const newStart = newEnd - currentRange;

    setZoomDomain([newStart, newEnd]);
  }, [sortedData, zoomDomain]);

  const handleZoomIn = useCallback(() => {
    if (!sortedData || sortedData.length === 0) return;

    isBrushChangingRef.current = false;
    if (!zoomDomain) {
      const firstItem = sortedData[0];
      const lastItem = sortedData[sortedData.length - 1];
      const firstTime = firstItem && typeof firstItem.time === 'number' ? firstItem.time : 0;
      const lastTime = lastItem && typeof lastItem.time === 'number' ? lastItem.time : 0;
      const center = (firstTime + lastTime) / 2;
      const range = (lastTime - firstTime) * 0.5;
      setZoomDomain([center - range / 2, center + range / 2]);
    } else {
      const center = (zoomDomain[0] + zoomDomain[1]) / 2;
      const range = (zoomDomain[1] - zoomDomain[0]) * 0.5;
      setZoomDomain([center - range / 2, center + range / 2]);
    }
  }, [sortedData, zoomDomain]);

  const handleZoomOut = useCallback(() => {
    if (!sortedData || sortedData.length === 0) return;

    isBrushChangingRef.current = false;
    if (!zoomDomain) {
      return;
    }

    const center = (zoomDomain[0] + zoomDomain[1]) / 2;
    const range = (zoomDomain[1] - zoomDomain[0]) * 2;
    const firstItem = sortedData[0];
    const lastItem = sortedData[sortedData.length - 1];
    const firstTime = firstItem && typeof firstItem.time === 'number' ? firstItem.time : 0;
    const lastTime = lastItem && typeof lastItem.time === 'number' ? lastItem.time : 0;

    const newStart = Math.max(firstTime, center - range / 2);
    const newEnd = Math.min(lastTime, center + range / 2);

    if (newEnd - newStart >= lastTime - firstTime) {
      setZoomDomain(null);
      if (showBrush) {
        setBrushStartIndex(0);
        setBrushEndIndex(sortedData.length - 1);
      }
    } else {
      setZoomDomain([newStart, newEnd]);
    }
  }, [sortedData, zoomDomain, showBrush]);

  const handleResetZoom = useCallback(() => {
    isBrushChangingRef.current = false;
    setZoomDomain(null);
    if (showBrush) {
      setBrushStartIndex(0);
      setBrushEndIndex(sortedData.length - 1);
    }
    setSelectionStart(null);
    setSelectionEnd(null);
  }, [sortedData.length, showBrush]);

  const hasSecondarySeries = useMemo(() => {
    return series.some((s) => s.yAxisId === 'secondary');
  }, [series]);

  const hasRightYAxis = useMemo(() => {
    return yAxes.some((axis) => axis.id === 'secondary') || hasSecondarySeries;
  }, [yAxes, hasSecondarySeries]);

  const getXValueFromMouse = useCallback(
    (clientX: number, containerRect: DOMRect, margin: { left: number; right: number }) => {
      const chartWidth = containerRect.width - margin.left - margin.right;
      const x = clientX - containerRect.left - margin.left;
      const ratio = x / chartWidth;

      const currentDomain = xDomain;
      if (Array.isArray(currentDomain) && currentDomain.length === 2) {
        const [min, max] = currentDomain;
        if (typeof min === 'number' && typeof max === 'number') {
          return min + (max - min) * ratio;
        }
      }

      if (sortedData.length > 0) {
        const firstItem = sortedData[0];
        const lastItem = sortedData[sortedData.length - 1];
        const firstTime = firstItem && typeof firstItem.time === 'number' ? firstItem.time : 0;
        const lastTime = lastItem && typeof lastItem.time === 'number' ? lastItem.time : 0;
        return firstTime + (lastTime - firstTime) * ratio;
      }

      return 0;
    },
    [xDomain, sortedData]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!containerRef.current || !sortedData || sortedData.length === 0) return;
      if (e.button !== 0) return;

      const rect = containerRef.current.getBoundingClientRect();
      const margin = { left: 60, right: hasRightYAxis ? 50 : 30 };
      const xValue = getXValueFromMouse(e.clientX, rect, margin);
      const pixelX = e.clientX - rect.left;

      if (e.shiftKey && zoomDomain) {
        setIsPanning(true);
        setPanStart(xValue);
      } else if (e.ctrlKey || e.metaKey) {
        setIsSelecting(true);
        setSelectionStart(xValue);
        setSelectionEnd(null);
        setSelectionPixelStart(pixelX);
        setSelectionPixelEnd(null);
      }
    },
    [sortedData, zoomDomain, hasRightYAxis, getXValueFromMouse]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!containerRef.current || !sortedData || sortedData.length === 0) return;

      const rect = containerRef.current.getBoundingClientRect();
      const margin = { left: 60, right: hasRightYAxis ? 50 : 30 };
      const xValue = getXValueFromMouse(e.clientX, rect, margin);
      const pixelX = e.clientX - rect.left;

      if (isPanning && panStart !== null && zoomDomain) {
        const delta = panStart - xValue;
        const currentRange = zoomDomain[1] - zoomDomain[0];
        const firstItem = sortedData[0];
        const lastItem = sortedData[sortedData.length - 1];
        const firstTime = firstItem && typeof firstItem.time === 'number' ? firstItem.time : 0;
        const lastTime = lastItem && typeof lastItem.time === 'number' ? lastItem.time : 0;

        const newStart = Math.max(firstTime, Math.min(lastTime - currentRange, zoomDomain[0] + delta));
        const newEnd = Math.min(lastTime, Math.max(firstTime + currentRange, zoomDomain[1] + delta));

        setZoomDomain([newStart, newEnd]);
        setPanStart(xValue);
      } else if (isSelecting && selectionStart !== null) {
        setSelectionEnd(xValue);
        setSelectionPixelEnd(pixelX);
      }
    },
    [isPanning, isSelecting, panStart, selectionStart, sortedData, zoomDomain, hasRightYAxis, getXValueFromMouse]
  );

  const handleMouseUp = useCallback(() => {
    if (isSelecting && selectionStart !== null && selectionEnd !== null) {
      const start = Math.min(selectionStart, selectionEnd);
      const end = Math.max(selectionStart, selectionEnd);

      if (Math.abs(end - start) > 1000) {
        const firstItem = sortedData[0];
        const lastItem = sortedData[sortedData.length - 1];
        const firstTime = firstItem && typeof firstItem.time === 'number' ? firstItem.time : 0;
        const lastTime = lastItem && typeof lastItem.time === 'number' ? lastItem.time : 0;

        const clampedStart = Math.max(firstTime, Math.min(lastTime, start));
        const clampedEnd = Math.max(firstTime, Math.min(lastTime, end));

        if (clampedEnd > clampedStart) {
          setZoomDomain([clampedStart, clampedEnd]);
        }
      }
    }

    setIsSelecting(false);
    setIsPanning(false);
    setSelectionStart(null);
    setSelectionEnd(null);
    setPanStart(null);
    setSelectionPixelStart(null);
    setSelectionPixelEnd(null);
  }, [isSelecting, isPanning, selectionStart, selectionEnd, sortedData]);

  const referenceArea = useMemo(() => {
    if (!isSelecting || selectionStart === null || selectionEnd === null) return null;
    const start = Math.min(selectionStart, selectionEnd);
    const end = Math.max(selectionStart, selectionEnd);
    return { x1: start, x2: end };
  }, [isSelecting, selectionStart, selectionEnd]);

  const hasPrimarySeries = useMemo(() => {
    return series.some((s) => s.yAxisId === 'primary');
  }, [series]);
  
  const primaryTickFormatter = useMemo(() => {
    const dataRange = yAxisDataRanges.primary;
    const min = dataRange ? dataRange.min : undefined;
    const max = dataRange ? dataRange.max : undefined;
    return (value: number) => formatYAxisTick(value, min, max);
  }, [yAxisDataRanges.primary]);

  const secondaryTickFormatter = useMemo(() => {
    const dataRange = yAxisDataRanges.secondary;
    const min = dataRange ? dataRange.min : undefined;
    const max = dataRange ? dataRange.max : undefined;
    return (value: number) => formatYAxisTick(value, min, max);
  }, [yAxisDataRanges.secondary]);

  const primaryYAxis = yAxes.find((axis) => axis.id === 'primary') || 
    (hasPrimarySeries ? {
      id: 'primary' as const,
      label: 'Eje Primario',
      orientation: 'left' as const,
      tickFormatter: primaryTickFormatter,
    } : null);
  const secondaryYAxis = yAxes.find((axis) => axis.id === 'secondary') || 
    (hasSecondarySeries ? {
      id: 'secondary' as const,
      label: 'Eje Secundario',
      orientation: 'right' as const,
      tickFormatter: secondaryTickFormatter,
    } : null);

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', height: isWidget ? '100%' : height, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: isWidget ? 200 : height }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Cargando datos...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%', height: isWidget ? '100%' : height, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: isWidget ? 200 : height, p: 2 }}>
        <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>
      </Box>
    );
  }

  if (!sortedData || sortedData.length === 0) {
    return (
      <Box sx={{ width: '100%', height: isWidget ? '100%' : height, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: isWidget ? 200 : height, p: 2 }}>
        <Alert severity="info" sx={{ width: '100%' }}>{emptyMessage || 'No hay datos disponibles.'}</Alert>
      </Box>
    );
  }

  if (!hasData) {
    return (
      <Box sx={{ width: '100%', height: isWidget ? '100%' : height, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: isWidget ? 200 : height, p: 2 }}>
        <Alert severity="warning" sx={{ width: '100%' }}>No hay datos válidos para las series seleccionadas.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: isWidget ? '100%' : height, display: 'flex', flexDirection: 'column', minHeight: isWidget ? 250 : height, position: 'relative', minWidth: 0 }}>
      {title && !isWidget && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: theme.typography.h6.fontFamily,
              fontWeight: theme.typography.h6.fontWeight,
              color: theme.palette.text.primary,
            }}
          >
            {title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Zoom in">
              <IconButton size="small" onClick={handleZoomIn}>
                <ZoomIn />
              </IconButton>
            </Tooltip>
            <Tooltip title="Zoom out">
              <IconButton size="small" onClick={handleZoomOut} disabled={!zoomDomain}>
                <ZoomOut />
              </IconButton>
            </Tooltip>
            <Tooltip title="Navegar atrás">
              <IconButton size="small" onClick={handleNavigateBack} disabled={!zoomDomain}>
                <NavigateBefore />
              </IconButton>
            </Tooltip>
            <Tooltip title="Navegar adelante">
              <IconButton size="small" onClick={handleNavigateForward} disabled={!zoomDomain}>
                <NavigateNext />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          flex: 1,
          minHeight: isWidget ? 200 : 300,
          position: 'relative',
          cursor: isWidget && !showBrush ? 'default' : (isSelecting ? 'crosshair' : isPanning ? 'grabbing' : 'default'),
          userSelect: 'none',
          '& .recharts-brush': {
            '& .recharts-brush-traveller': {
              fill: theme.palette.secondary.main,
              stroke: theme.palette.secondary.dark,
              strokeWidth: 2,
              '&:hover': {
                fill: theme.palette.secondary.dark,
              },
            },
            '& .recharts-brush-slide': {
              fill: theme.palette.secondary.main,
              fillOpacity: 0.2,
              stroke: theme.palette.secondary.main,
              strokeWidth: 2,
            },
          },
        }}
        onMouseDown={isWidget && !showBrush ? undefined : handleMouseDown}
        onMouseMove={isWidget && !showBrush ? undefined : handleMouseMove}
        onMouseUp={isWidget && !showBrush ? undefined : handleMouseUp}
        onMouseLeave={isWidget && !showBrush ? undefined : handleMouseUp}
      >
        {isSelecting && selectionPixelStart !== null && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 5,
              pointerEvents: 'none',
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)',
            }}
          >
            {selectionPixelEnd !== null && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  backgroundColor: 'transparent',
                  borderLeft: `3px solid ${theme.palette.secondary.main}`,
                  borderRight: `3px solid ${theme.palette.secondary.main}`,
                  boxShadow: `0 0 0 2px ${theme.palette.secondary.main}40`,
                }}
                style={{
                  left: `${Math.min(selectionPixelStart, selectionPixelEnd)}px`,
                  width: `${Math.abs(selectionPixelEnd - selectionPixelStart)}px`,
                }}
              />
            )}
          </Box>
        )}
        {zoomDomain && (showBrush || !isWidget) && (
          <Button
            size="small"
            variant="contained"
            onClick={handleResetZoom}
            startIcon={<RestartAlt />}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10,
              minWidth: 'auto',
              px: 1.5,
              py: 0.5,
              fontSize: '0.75rem',
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.secondary.contrastText,
              boxShadow: theme.shadows[4],
              '&:hover': {
                backgroundColor: theme.palette.secondary.dark,
                boxShadow: theme.shadows[6],
              },
            }}
          >
            Reset Zoom
          </Button>
        )}
        <Box sx={{ width: '100%', height: isWidget ? '100%' : height, minHeight: isWidget ? 200 : height, minWidth: 0, flex: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
          <LineChart
            ref={chartRef}
            data={sortedData}
            margin={{
              top: isWidget ? 5 : 20,
              right: hasRightYAxis ? (isWidget ? 35 : 80) : (isWidget ? 10 : 30),
              left: isWidget ? 30 : 80,
              bottom: isWidget ? (showBrush ? (series.length > 3 ? 80 : 60) : (series.length > 3 ? 45 : 30)) : 80,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} opacity={0.5} />
            <XAxis
              dataKey="time"
              type="number"
              scale={xAxisVariableKey ? 'linear' : (xAxisMode === 'seconds' ? 'linear' : 'time')}
              domain={xDomain}
              allowDataOverflow={false}
              allowDecimals={Boolean(xAxisVariableKey) || xAxisMode === 'seconds'}
              padding={{ left: 0, right: 0 }}
              tickCount={xAxisTickCount}
              tickFormatter={(value) => {
                if (xAxisVariableKey) {
                  const n = Number(value);
                  return isFinite(n) ? (Math.abs(n) < 0.01 || Math.abs(n) > 1000 ? n.toExponential(1) : n.toFixed(2)) : String(value);
                }
                if (xAxisMode === 'seconds') {
                  return `${Number(value).toFixed(1)} s`;
                }
                const date = new Date(value);
                if (isWidget) {
                  return date.toLocaleString('es-ES', {
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                  });
                }
                return date.toLocaleString('es-ES', {
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                });
              }}
              angle={isWidget ? -30 : -45}
              textAnchor="end"
              height={isWidget ? 30 : 60}
              tick={{ fill: theme.palette.text.secondary, fontSize: isWidget ? 8 : 11 }}
              stroke={theme.palette.divider}
              label={!isWidget ? {
                value: xAxisVariableLabel || xAxisLabel || (xAxisMode === 'seconds' ? 'Tiempo (s)' : 'Tiempo'),
                position: 'insideBottom',
                offset: -5,
                style: {
                  textAnchor: 'middle',
                  fill: theme.palette.text.primary,
                  fontSize: 12,
                  fontWeight: 600,
                },
              } : undefined}
            />
            {primaryYAxis && (
              <YAxis
                yAxisId="primary"
                orientation={primaryYAxis.orientation || 'left'}
                tickFormatter={primaryTickFormatter}
                stroke={theme.palette.text.secondary}
                tick={{ fill: theme.palette.text.secondary, fontSize: isWidget ? 8 : 11 }}
                width={isWidget ? 30 : 60}
                domain={yAxisDomains.primary || ['auto', 'auto']}
                tickCount={calculateYAxisTickCount(yAxisDomains.primary)}
                label={!isWidget ? {
                  value: primaryYAxis.label,
                  angle: -90,
                  position: 'insideLeft',
                  offset: -10,
                  style: {
                    textAnchor: 'middle',
                    fill: theme.palette.text.primary,
                    fontSize: 12,
                    fontWeight: 600,
                  },
                } : undefined}
              />
            )}
            {secondaryYAxis && (
              <YAxis
                yAxisId="secondary"
                orientation={secondaryYAxis.orientation || 'right'}
                tickFormatter={secondaryTickFormatter}
                stroke={theme.palette.text.secondary}
                tick={{ fill: theme.palette.text.secondary, fontSize: isWidget ? 8 : 11 }}
                width={isWidget ? 30 : 60}
                domain={yAxisDomains.secondary || ['auto', 'auto']}
                tickCount={calculateYAxisTickCount(yAxisDomains.secondary)}
                label={!isWidget ? {
                  value: secondaryYAxis.label,
                  angle: 90,
                  position: 'insideRight',
                  offset: -10,
                  style: {
                    textAnchor: 'middle',
                    fill: theme.palette.text.primary,
                    fontSize: 12,
                    fontWeight: 600,
                  },
                } : undefined}
              />
            )}
            <RechartsTooltip
              content={({ active, payload, label }: any) => {
                if (!active || !payload || !payload.length) {
                  return null;
                }
                let xLabel: string;
                const xLabelTitle = xAxisVariableLabel || (xAxisMode === 'seconds' ? 'Tiempo' : 'Fecha');
                if (xAxisVariableKey) {
                  const n = Number(label);
                  xLabel = isFinite(n) ? (Math.abs(n) < 0.01 || Math.abs(n) > 1000 ? n.toExponential(2) : n.toFixed(3)) : String(label);
                } else if (xAxisMode === 'seconds') {
                  xLabel = `${Number(label).toFixed(2)} s`;
                } else {
                  const fecha = typeof label === 'number' ? new Date(label) : new Date(label);
                  xLabel = fecha.toLocaleString('es-ES', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  });
                }
                return (
                  <Paper
                    sx={{
                      p: isWidget ? 0.75 : 1.5,
                      backgroundColor: alpha(theme.palette.background.paper, 0.95),
                      border: `1px solid ${theme.palette.divider}`,
                      boxShadow: theme.shadows[4],
                      maxWidth: isWidget ? 200 : 'none',
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isWidget ? 0.25 : 0.5 }}>
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ 
                          fontSize: isWidget ? '0.7rem' : '0.75rem',
                        }}
                      >
                        {xLabelTitle}: {xLabel}
                      </Typography>
                      {payload.map((entry: any, index: number) => {
                        const serie = series.find((s) => s.dataKey === entry.dataKey);
                        const serieColor = serie?.color || entry.color;
                        const serieLabel = serie?.label || entry.dataKey;
                        const dataRange = serie?.yAxisId === 'primary' 
                          ? yAxisDataRanges.primary 
                          : serie?.yAxisId === 'secondary' 
                          ? yAxisDataRanges.secondary 
                          : undefined;
                        const min = dataRange ? dataRange.min : undefined;
                        const max = dataRange ? dataRange.max : undefined;
                        const value = typeof entry.value === 'number' 
                          ? formatYAxisTick(entry.value, min, max) 
                          : entry.value;
                        return (
                          <Typography
                            key={index}
                            variant={isWidget ? "caption" : "body2"}
                            component="div"
                            sx={{ 
                              fontWeight: 600, 
                              color: serieColor,
                              fontSize: isWidget ? '0.7rem' : '0.875rem',
                              display: 'block',
                            }}
                          >
                            {serieLabel}: {value} {serie?.unidad || ''}
                          </Typography>
                        );
                      })}
                    </Box>
                  </Paper>
                );
              }}
            />
            <Legend
              wrapperStyle={{
                paddingTop: isWidget ? '4px' : '20px',
                paddingBottom: isWidget ? '2px' : '0px',
                fontFamily: theme.typography.fontFamily,
              }}
              iconType="line"
              formatter={(value: any) => {
                const serie = series.find((s) => s.dataKey === value);
                return serie?.label || value;
              }}
              content={({ payload }: any) => {
                if (!payload || !payload.length) return null;
                const maxItems = isWidget ? (height < 250 ? 2 : 3) : payload.length;
                const displayPayload = isWidget ? payload.slice(0, maxItems) : payload;
                
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: isWidget ? 'wrap' : 'wrap',
                      gap: isWidget ? 0.5 : 2,
                      justifyContent: isWidget ? 'flex-start' : 'center',
                      pt: isWidget ? 0.5 : 2,
                      px: isWidget ? 0 : 0,
                    }}
                  >
                    {displayPayload.map((entry: any, index: number) => {
                      const serie = series.find((s) => s.dataKey === entry.dataKey);
                      const color = serie?.color || entry.color;
                      return (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: isWidget ? 0.75 : 1,
                            py: isWidget ? 0.25 : 0.5,
                            borderRadius: 1,
                            backgroundColor: alpha(color, 0.1),
                            '&:hover': {
                              backgroundColor: alpha(color, 0.2),
                            },
                            transition: 'background-color 0.2s',
                          }}
                        >
                          <Box
                            sx={{
                              width: isWidget ? 8 : 12,
                              height: isWidget ? 8 : 12,
                              borderRadius: '50%',
                              backgroundColor: color,
                              border: `2px solid ${color}`,
                              flexShrink: 0,
                            }}
                          />
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontSize: isWidget ? 8 : 11, 
                              color: theme.palette.text.primary,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: isWidget ? '70px' : 'none',
                              lineHeight: 1.2,
                            }}
                          >
                            {serie?.label || entry.dataKey}
                          </Typography>
                        </Box>
                      );
                    })}
                    {isWidget && payload.length > maxItems && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: 9, 
                          color: theme.palette.text.secondary,
                          alignSelf: 'center',
                          px: 0.75,
                        }}
                      >
                        +{payload.length - maxItems} más
                      </Typography>
                    )}
                  </Box>
                );
              }}
            />
            {referenceArea && (
              <ReferenceArea
                x1={referenceArea.x1}
                x2={referenceArea.x2}
                stroke={theme.palette.secondary.main}
                strokeWidth={2}
                strokeOpacity={0.8}
                fill={theme.palette.secondary.main}
                fillOpacity={0.25}
                strokeDasharray="5 5"
              />
            )}
            {referenceLines.map((refLine, index) => (
              <ReferenceLine
                key={`ref-line-${index}`}
                y={refLine.y}
                stroke={refLine.stroke}
                strokeDasharray={refLine.strokeDasharray}
                label={{
                  value: refLine.label || '',
                  position: 'insideTopRight',
                  fill: refLine.stroke,
                  fontSize: 11,
                  fontWeight: 600,
                }}
                yAxisId={refLine.yAxisId || 'primary'}
              />
            ))}
            {series.map((serie) => {
              // Ocultar puntos cuando hay muchos datos para mejor visualización
              // Mostrar puntos solo si hay menos de 500 puntos o en widgets pequeños
              const shouldShowDots = isWidget ? false : (sortedData.length < 500);
              
              return (
                <Line
                  key={serie.key}
                  yAxisId={serie.yAxisId}
                  type="linear"
                  dataKey={serie.dataKey}
                  stroke={serie.color}
                  strokeWidth={isWidget ? 1.5 : 2.5}
                  dot={shouldShowDots ? { r: 1.5, fill: serie.color, strokeWidth: 0 } : false}
                  activeDot={{ r: isWidget ? 4 : 6, fill: serie.color, strokeWidth: 2, stroke: theme.palette.background.paper }}
                  name={serie.label}
                  connectNulls={true}
                  isAnimationActive={false}
                  strokeOpacity={1}
                />
              );
            })}
            {showBrush && (
              <Brush
                dataKey="time"
                height={45}
                startIndex={brushStartIndex}
                endIndex={brushEndIndex}
                onChange={handleBrushChange}
                tickFormatter={(value) => {
                  if (xAxisMode === 'seconds') {
                    return `${Number(value).toFixed(1)} s`;
                  }
                  const date = new Date(value);
                  return date.toLocaleString('es-ES', {
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  });
                }}
                fill={theme.palette.background.paper}
                stroke={theme.palette.primary.main}
                strokeWidth={2}
              />
            )}
          </LineChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      {!isWidget && (
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontFamily: theme.typography.fontFamily,
            }}
          >
            {zoomDomain
              ? `Mostrando ${visibleData.length} de ${sortedData.length} puntos`
              : `Total de puntos: ${sortedData.length} registros`}
            {isSelecting && ' - Arrastra para seleccionar área'}
            {isPanning && ' - Mantén Shift y arrastra para desplazarte'}
            {!isSelecting && !isPanning && zoomDomain && ' - Mantén Ctrl y arrastra para hacer zoom'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

